import {
  BASE_FREE_SHIPPING_TEXT,
  BASE_NON_PERK_TEXT,
  BASE_PERK_TEXT,
  BUSINESS_UNKNOWN_PERK_TEXT,
  HOLIDAY_NON_PERK_TEXT,
  HOLIDAY_PERK_TEXT,
  PRIME_NON_PERK_TEXT,
  PRIME_PERK_TEXT,
  VIP_NON_PERK_TEXT,
  VIP_PERK_TEXT
} from 'constants/shipOptionMessaging';
import {
  GET_SHIP_OPTION_LIST_SUCCESS,
  SET_SHIP_OPTION_DATA_LOADING,
  SET_SHIP_OPTIONS_NOT_LOADED
} from 'store/ducks/shipOption/types';
import { CONFIGURE_CHECKOUT_SUCCESS } from 'store/ducks/checkout/types';
import {
  DYNAMIC_NO_PERK_SHIP_OPTIONS_MAP,
  DYNAMIC_PRIME_SHIP_OPTIONS_MAP,
  DYNAMIC_VIP_SHIP_OPTIONS_MAP,
  NON_STANDARD_SHIP_OPTIONS_MAP
} from 'constants/nonStandardShipOptions';
import { toUSD } from 'helpers/NumberFormats';
import {
  dateFromPromise,
  getEndDateFromRange,
  getLabelsFromMap,
  isBaseZapposShipping,
  isFreeHolidayShipping,
  isFreeShippingSpeed,
  isOneDayVipShippingPerk,
  isTwoDayPrimeShippingPerk,
  isZapposShipping
} from 'helpers/CheckoutUtils';
import { trackError } from 'helpers/ErrorUtils';
import { DIGITS, MONTHS } from 'common/regex';

const defaultState = {
  savedShipOptions: [],
  isLoading: false,
  isLoaded: false,
  hasNextWow: false
};

export default function shipOptions(state = defaultState, action = {}) {
  const {
    type,
    payload,
    isFilteredShipOptions,
    isDynamicLabels,
    showCheckoutNonStandardShipOptionLabels
  } = action;

  switch (type) {
    case SET_SHIP_OPTIONS_NOT_LOADED: {
      return { ...state, isLoaded: false };
    }
    case CONFIGURE_CHECKOUT_SUCCESS: {
      const {
        deliveryOptions,
        purchaseStatus: { purchaseId, shippingBenefitReason, shipmentSpeed, deliveryGroups, productList, groupDeliveryOptions }
      } = payload;
      const { isLoaded, lineItemDeliveryOptions, groupDeliveryOptions: prevGroupDeliveryOptions } = state;
      let newState = state;

      if (deliveryOptions) {
        newState = addSavedShipOptionsToState(newState, deliveryOptions);
      } else {
        newState = { ...newState, isLoading: false };
      }

      newState = { ...newState, changedPromiseDates: [] };
      newState = addDeliveryPromiseToLineItemDelivery(newState, deliveryGroups);
      newState = storeDexDeliveryPromise(newState, groupDeliveryOptions);
      newState = transformLineItemDeliveryOptionsWithLabelsAndSort(newState, isDynamicLabels, showCheckoutNonStandardShipOptionLabels, shippingBenefitReason);
      newState = addPurchaseDeliveryToLineItemDelivery(newState, shippingBenefitReason, shipmentSpeed);
      newState = storePromiseDateHasChangedPceToPce(newState, lineItemDeliveryOptions, purchaseId);
      newState = storePromiseDateHasChangedDexToDex(newState, prevGroupDeliveryOptions, purchaseId);
      newState = storePromiseDateHasChangedDexToPce(newState, purchaseId);
      newState = storeGroupDigitalDeliveryStatus(newState, productList);

      if (isFilteredShipOptions && !isLoaded) {
        newState = filterShipOptions(newState);
      }

      return newState;
    }

    case GET_SHIP_OPTION_LIST_SUCCESS: {
      return addSavedShipOptionsToState(state, payload);
    }

    case SET_SHIP_OPTION_DATA_LOADING: {
      return { ...state, isLoading: payload };
    }

    default: {
      return state;
    }
  }
}

const MONTH_TO_NUMERIC_MAP = {
  'January': 1,
  'February': 2,
  'March': 3,
  'April': 4,
  'May': 5,
  'June': 6,
  'July': 7,
  'August': 8,
  'September': 9,
  'October': 10,
  'November': 11,
  'December': 12
};

const HIGHLIGHTED_SHIPPING_OPTION_MAP = {
  vip: {
    'next-business': true,
    'next': true
  },
  prime: {
    'second': true
  },
  base: {
    'next-wow': true,
    'std-us-non48': true
  },
  holiday: {
    'second': true,
    'next-business': true,
    'next': true
  }
};

const promiseComparator = (a, b) => {
  const { deliveryOption: { deliveryPromise: { displayString: aDisplayString } } } = a;
  const { deliveryOption: { deliveryPromise: { displayString: bDisplayString } } } = b;

  const aDatePart = aDisplayString.split(',').pop().trim();
  const [aMonth, aDay] = aDatePart.split(' ');

  const bDatePart = bDisplayString.split(',').pop().trim();
  const [bMonth, bDay] = bDatePart.split(' ');

  if (MONTH_TO_NUMERIC_MAP[aMonth] < MONTH_TO_NUMERIC_MAP[bMonth] || (MONTH_TO_NUMERIC_MAP[aMonth] === 12 && MONTH_TO_NUMERIC_MAP[bMonth] === 1)) {
    return 1;
  }

  if (MONTH_TO_NUMERIC_MAP[aMonth] > MONTH_TO_NUMERIC_MAP[bMonth]) {
    return -1;
  }

  if (parseInt(aDay, 10) < parseInt(bDay, 10)) {
    return 1;
  }

  if (parseInt(aDay, 10) > parseInt(bDay, 10)) {
    return -1;
  }

  return 0;
};

export const getIsHighlighted = ({ shippingBenefitReason, shipmentSpeed }) => (isOneDayVipShippingPerk(shippingBenefitReason) && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.vip[shipmentSpeed])
  || (isTwoDayPrimeShippingPerk(shippingBenefitReason) && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.prime[shipmentSpeed])
  || (isFreeHolidayShipping(shippingBenefitReason) && shipmentSpeed !== 'next-wow')
  || (isBaseZapposShipping(shippingBenefitReason) && isFreeShippingSpeed(shipmentSpeed));

export const getLogo = ({ shippingBenefitReason, shipmentSpeed }) => {
  if (isOneDayVipShippingPerk(shippingBenefitReason) && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.vip[shipmentSpeed]) {
    return 'vip';
  } else if (isTwoDayPrimeShippingPerk(shippingBenefitReason) && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.prime[shipmentSpeed]) {
    return 'prime';
  } else if (isFreeHolidayShipping(shippingBenefitReason) && shipmentSpeed !== 'next-wow') {
    return 'holiday';
  } else if (isBaseZapposShipping(shippingBenefitReason) && isFreeShippingSpeed(shipmentSpeed)) {
    return 'zappos';
  }
  return false;
};

export const getDescription = ({ shippingBenefitReason, shipmentSpeed, price }) => {
  const isVipPerk = isOneDayVipShippingPerk(shippingBenefitReason);
  const isPrimePerk = isTwoDayPrimeShippingPerk(shippingBenefitReason);
  const isHolidayPerk = isFreeHolidayShipping(shippingBenefitReason);
  const isBasePerk = isBaseZapposShipping(shippingBenefitReason);
  const isZappos = isZapposShipping(shippingBenefitReason);

  if (isVipPerk && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.vip[shipmentSpeed]) {
    return VIP_PERK_TEXT;
  } else if (isVipPerk && shipmentSpeed !== 'next-wow') {
    return VIP_NON_PERK_TEXT;
  } else if (isPrimePerk && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.prime[shipmentSpeed]) {
    return PRIME_PERK_TEXT;
  } else if (isHolidayPerk && shipmentSpeed !== 'next-wow') {
    return HOLIDAY_PERK_TEXT;
  } else if (isBasePerk && isFreeShippingSpeed(shipmentSpeed)) {
    return BASE_PERK_TEXT;
  } else if (isZappos && isFreeShippingSpeed(shipmentSpeed)) {
    return BASE_NON_PERK_TEXT;
  } else if (price === 0) {
    return BASE_FREE_SHIPPING_TEXT;
  } else {
    return `${toUSD(price)} Paid Shipping`;
  }
};

export const getSelectedDescription = ({ isBusinessUnknown, shippingBenefitReason, shipmentSpeed, price }) => {
  if (isBusinessUnknown) {
    return;
  }

  const isVipPerk = isOneDayVipShippingPerk(shippingBenefitReason);
  const isPrimePerk = isTwoDayPrimeShippingPerk(shippingBenefitReason);
  const isHolidayPerk = isFreeHolidayShipping(shippingBenefitReason);
  const isZappos = isZapposShipping(shippingBenefitReason);

  if (isVipPerk && shipmentSpeed !== 'next-wow') {
    return VIP_NON_PERK_TEXT;
  } else if (isPrimePerk && shipmentSpeed === 'second') {
    return PRIME_NON_PERK_TEXT;
  } else if (isHolidayPerk && shipmentSpeed !== 'next-wow') {
    return HOLIDAY_NON_PERK_TEXT;
  } else if (isZappos && (isFreeShippingSpeed(shipmentSpeed))) {
    return BASE_NON_PERK_TEXT;
  } else if (price === 0) {
    return BASE_FREE_SHIPPING_TEXT;
  } else {
    return `${toUSD(price)} Paid Shipping`;
  }
};

export function storeDexDeliveryPromise(state, groupDeliveryOptions) {
  return { ...state, groupDeliveryOptions };
}
export function storeGroupDigitalDeliveryStatus(state, productList) {
  if (!productList?.length) {
    return state;
  }

  const { lineItemDeliveryOptions } = state;
  const transformedLineItemDeliveryOptions = [];

  lineItemDeliveryOptions.forEach(item => {
    const { lineItemIds } = item;

    if (lineItemIds.length > 1) {
      transformedLineItemDeliveryOptions.push({
        ...item,
        isDigitalDelivery: false
      });
    } else {
      const isDigitalDelivery = !!productList.find(li => li.lineItemId === lineItemIds[0])?.gcCustomization;
      transformedLineItemDeliveryOptions.push({
        ...item,
        isDigitalDelivery
      });
    }
  });

  return { ...state, lineItemDeliveryOptions: transformedLineItemDeliveryOptions };
}

/*
  This generates the content for the box that appears above the ship options, ie, the perk box
*/
export function addPurchaseDeliveryToLineItemDelivery(state, shippingBenefitReason, shipmentSpeed) {
  const { lineItemDeliveryOptions } = state;
  const isHighlighted = getIsHighlighted({ shippingBenefitReason, shipmentSpeed });
  const logo = getLogo({ shippingBenefitReason, shipmentSpeed });
  const collection = [];

  lineItemDeliveryOptions.forEach(item => {
    const { deliveryOptions } = item;
    const option = deliveryOptions.find(option => option.name === shipmentSpeed) || { deliveryPromise: { displayString: 'Delivery' } };
    const { deliveryPromise: { displayString }, price } = option;
    const purchaseDelivery = {
      isHighlighted,
      logo,
      promise: getEndDateFromRange(dateFromPromise(displayString)),
      description: displayString ? getDescription({ shippingBenefitReason, shipmentSpeed, price }) : BUSINESS_UNKNOWN_PERK_TEXT
    };

    collection.push({ ...item, purchaseDelivery });
  });

  return { ...state, lineItemDeliveryOptions: collection };
}

/*
  The promises (and duration) that currently exist in the response are not valid. This replaces them with valid values.
*/
export const addDeliveryPromiseToLineItemDelivery = (state, deliveryGroups) => {
  const { lineItemDeliveryOptions } = state;
  const transformedLineItemDeliveryOptions = [];

  lineItemDeliveryOptions.forEach(option => {
    const { deliveryOptions } = option;
    const transformedDeliveryOptions = [];

    deliveryOptions.forEach(opt => {
      const { name } = opt;
      const val = deliveryGroups.filter(group => group.deliveryOption.name === name).sort(promiseComparator)[0] || { deliveryOption: {} }; // of all possible dates, we only want the furthest one out
      const {
        deliveryPromise = { displayString: '' },
        deliveryDuration = { displayString: '' }
      } = val.deliveryOption;
      transformedDeliveryOptions.push({ ...opt, deliveryPromise, deliveryDuration });
    });
    transformedLineItemDeliveryOptions.push({ ...option, deliveryOptions: transformedDeliveryOptions });
  });

  return { ...state, lineItemDeliveryOptions: transformedLineItemDeliveryOptions };
};

const transformLineItemDeliveryOptionsWithLabelsAndSort = (newState, isDynamicLabels, showCheckoutNonStandardShipOptionLabels, shippingBenefitReason) => {
  const { lineItemDeliveryOptions } = newState;
  const transformedLineItemDeliveryOptions = [];

  let sorter = () => 0;
  let labels = NON_STANDARD_SHIP_OPTIONS_MAP;

  if (isDynamicLabels) {
    sorter = getSorter(shippingBenefitReason);
    labels = getLabels(shippingBenefitReason);
  }

  lineItemDeliveryOptions.forEach(({ deliveryOptions, lineItemIds }) => {
    const transformedDeliveryOptions = [...deliveryOptions].sort(sorter).map(option => {
      const { promise, price, name, deliveryPromise: { isBusinessUnknown } } = option;
      const description = getSelectedDescription({ isBusinessUnknown, shippingBenefitReason, shipmentSpeed: name, price });
      const label = showCheckoutNonStandardShipOptionLabels ? getLabelsFromMap(promise, labels) : promise;
      return { ...option, description, label };
    });
    transformedLineItemDeliveryOptions.push({ deliveryOptions: transformedDeliveryOptions, lineItemIds });
  });

  return { ...newState, lineItemDeliveryOptions: transformedLineItemDeliveryOptions };
};

export function determinePromiseChanges(previous, current, purchaseId, type) {
  const changedPromiseDates = [];
  try {
    current.forEach(group => {
      const { lineItemIds, deliveryOptions: nextDeliveryOptions } = group;
      const lineItemKey = lineItemIds.sort().join('');
      nextDeliveryOptions.forEach(option => {
        const {
          name,
          deliveryPromise: { displayString },
          deliveryDuration: { displayString: duration }
        } = option;

        const prevGroup = previous.find(el => el.lineItemIds.sort().join('') === lineItemKey);
        const prevOption = prevGroup?.deliveryOptions.find(el => el.name === name);

        if (prevOption) {
          const prevDisplayString = prevOption.deliveryPromise.displayString || '';
          const strippedPromise = dateFromPromise(displayString);
          const strippedPrevDisplayString = dateFromPromise(prevDisplayString);
          if (displayString !== prevDisplayString) {
            changedPromiseDates.push({
              name,
              duration,
              prevPromiseDate: prevDisplayString,
              promiseDate: displayString,
              purchaseId,
              sameOtherThanType: strippedPromise === strippedPrevDisplayString,
              type
            });
          }
        }
      });
    });
  } catch (error) {
    trackError('NON-FATAL', 'Could not determine if promise data changed', error);
  }
  return changedPromiseDates;
}

export function storePromiseDateHasChangedDexToPce(state, purchaseId) {
  const { groupDeliveryOptions, lineItemDeliveryOptions, changedPromiseDates = [] } = state;

  if (!lineItemDeliveryOptions?.length || !groupDeliveryOptions?.length) {
    return state;
  }

  const changes = determinePromiseChanges(lineItemDeliveryOptions, groupDeliveryOptions, purchaseId, 'DEX_TO_PCE');

  return { ...state, changedPromiseDates: [...changedPromiseDates, ...changes] };
}

export function storePromiseDateHasChangedDexToDex(state, prevGroupDeliveryOptions, purchaseId) {
  const { groupDeliveryOptions, changedPromiseDates = [] } = state;

  if (!prevGroupDeliveryOptions?.length || !groupDeliveryOptions?.length) {
    return state;
  }

  const changes = determinePromiseChanges(prevGroupDeliveryOptions, groupDeliveryOptions, purchaseId, 'DEX_TO_DEX');

  return { ...state, changedPromiseDates: [...changedPromiseDates, ...changes] };
}

export function storePromiseDateHasChangedPceToPce(state, prevLineItemDeliveryOptions, purchaseId) {
  const { lineItemDeliveryOptions, changedPromiseDates = [] } = state;

  if (!prevLineItemDeliveryOptions?.length || !lineItemDeliveryOptions?.length) {
    return state;
  }

  const changes = determinePromiseChanges(prevLineItemDeliveryOptions, lineItemDeliveryOptions, purchaseId, 'PCE_TO_PCE');

  return { ...state, changedPromiseDates: [...changedPromiseDates, ...changes] };
}

const addSavedShipOptionsToState = (state, payload) => {
  const { groupDeliveryOptions: { lineItemDeliveryOptions, orderDeliveryOptions } } = payload;
  let savedShipOptions = orderDeliveryOptions;

  if (!orderDeliveryOptions.length) {
    const options = lineItemDeliveryOptions.find(item => item.deliveryOptions.length) || { deliveryOptions: [] };
    savedShipOptions = options.deliveryOptions;
  }

  return { ...state, savedShipOptions, lineItemDeliveryOptions, hasNextWow: checkForNextWow(savedShipOptions), isLoaded: true, isLoading: false };
};

export const getSorter = shippingBenefitReason => {
  if (isOneDayVipShippingPerk(shippingBenefitReason)) {
    return vipSorter;
  }

  if (isTwoDayPrimeShippingPerk(shippingBenefitReason)) {
    return primeSorter;
  }

  return noPerkSorter;
};

export const getLabels = shippingBenefitReason => {
  if (isOneDayVipShippingPerk(shippingBenefitReason)) {
    return DYNAMIC_VIP_SHIP_OPTIONS_MAP;
  }

  if (isTwoDayPrimeShippingPerk(shippingBenefitReason)) {
    return DYNAMIC_PRIME_SHIP_OPTIONS_MAP;
  }

  return DYNAMIC_NO_PERK_SHIP_OPTIONS_MAP;
};

export const vipSorter = (a, b) => {
  const { name: aName } = a;
  const { name: bName } = b;

  if (aName === 'next-business' || aName === 'next') {
    return -1;
  }

  if (aName === 'second' && bName === 'next-wow') {
    return -1;
  }

  return 1;
};

export const primeSorter = (a, b) => {
  const { name: aName } = a;
  const { name: bName } = b;

  if (aName === 'second') {
    return -1;
  }

  if ((aName === 'next-wow' && bName === 'next-business') || (aName === 'next-wow' && bName === 'next')) {
    return -1;
  }

  return 1;
};

export const noPerkSorter = (a, b) => {
  const { name: aName } = a;
  const { name: bName } = b;

  if (aName === 'next-wow') {
    return -1;
  }

  if ((aName === 'second' && bName === 'next-business') || aName === 'second' && bName === 'next') {
    return -1;
  }

  return 1;
};

export const checkForNextWow = savedShipOptions => !!savedShipOptions?.find(item => item.name === 'next-wow');

export function filterShipOptions(state) {
  const { lineItemDeliveryOptions } = state;
  const filteredLineItemDeliveryOptions = [];

  if (!lineItemDeliveryOptions?.length) {
    return state;
  }

  try {
    lineItemDeliveryOptions.forEach(lineItemDelivery => {
      const { deliveryOptions, isDigitalDelivery } = lineItemDelivery;

      if (isDigitalDelivery || deliveryOptions.length !== 3) {
        filteredLineItemDeliveryOptions.push(lineItemDelivery);
        return;
      }

      const nextWowOption = deliveryOptions.find(option => option.name === 'next-wow');
      const nextBusinessOption = deliveryOptions.find(option => option.name === 'next-business');
      const secondOption = deliveryOptions.find(option => option.name === 'second');

      if (!nextWowOption || !nextBusinessOption || !secondOption) {
        // we are only considering base case where zappos customer has the 3 standard options
        filteredLineItemDeliveryOptions.push(lineItemDelivery);
        return;
      }

      const nextWowPromise = getEndDateFromRange(dateFromPromise(nextWowOption.deliveryPromise?.displayString));
      const nextBusinessPromise = getEndDateFromRange(dateFromPromise(nextBusinessOption.deliveryPromise?.displayString));
      const secondPromise = getEndDateFromRange(dateFromPromise(secondOption.deliveryPromise?.displayString));

      const nextBusinessPrice = nextBusinessOption.price;
      const secondPrice = secondOption.price;

      const filteredOptions = [];
      const filteredShipSpeeds = [];

      if (!nextBusinessPromise.length || !secondPromise.length) {
        // if we are missing a promise in non next-wow options, no filtering should happen
        filteredOptions.push(
          { ...nextWowOption, isFiltered: false },
          { ...secondOption, isFiltered: false },
          { ...nextBusinessOption, isFiltered: false }
        );
        filteredLineItemDeliveryOptions.push({ ...lineItemDelivery, deliveryOptions: filteredOptions, filteredShipSpeeds });
        return;
      }

      if (nextWowPromise === nextBusinessPromise && nextWowPromise === secondPromise) {
        // case 1: IF all have same promise, THEN show only next-wow
        filteredOptions.push(
          { ...nextWowOption, isFiltered: false },
          { ...secondOption, isFiltered: true },
          { ...nextBusinessOption, isFiltered: true }
        );
        filteredShipSpeeds.push('next-business', 'second');
      } else if (isPromiseSameOrFasterThan(secondPromise, nextBusinessPromise)) {
        // case 2: IF second has same promise as next-business or is faster, THEN hide next-business
        filteredOptions.push(
          { ...nextWowOption, isFiltered: false },
          { ...secondOption, isFiltered: false },
          { ...nextBusinessOption, isFiltered: true }
        );
        filteredShipSpeeds.push('next-business');
      } else if (isPromiseSameOrFasterThan(nextBusinessPromise, secondPromise) && secondPrice === nextBusinessPrice) {
        // case 3: IF next-business is faster than second AND same price as second, THEN hide second
        filteredOptions.push(
          { ...nextWowOption, isFiltered: false },
          { ...secondOption, isFiltered: true },
          { ...nextBusinessOption, isFiltered: false }
        );
        filteredShipSpeeds.push('second');
      } else {
        // no filtering
        filteredOptions.push(
          { ...nextWowOption, isFiltered: false },
          { ...secondOption, isFiltered: false },
          { ...nextBusinessOption, isFiltered: false }
        );
      }

      filteredLineItemDeliveryOptions.push({ ...lineItemDelivery, deliveryOptions: filteredOptions, filteredShipSpeeds });
    });
  } catch (e) {
    return state;
  }

  return { ...state, lineItemDeliveryOptions: filteredLineItemDeliveryOptions };
}

const getMonthFromPromise = promise => (promise ? promise.match(MONTHS) : '');

const getDayFromPromise = promise => (promise ? parseInt(promise.match(DIGITS)[0], 10) : '');

const isPromiseSameOrFasterThan = (promise1, promise2) => {
  const month1 = getMonthFromPromise(promise1);
  const day1 = getDayFromPromise(promise1);

  const month2 = getMonthFromPromise(promise2);
  const day2 = getDayFromPromise(promise2);

  if (MONTH_TO_NUMERIC_MAP[month1] < MONTH_TO_NUMERIC_MAP[month2] || (MONTH_TO_NUMERIC_MAP[month1] === 12 && MONTH_TO_NUMERIC_MAP[month2] === 1)) {
    return true;
  }

  if (MONTH_TO_NUMERIC_MAP[month1] > MONTH_TO_NUMERIC_MAP[month2]) {
    return false;
  }

  return day1 > day2 ? false : true;
};
